import { makeRequest } from './utils/request';
import { VoiceAI } from './client';

export interface SignupOptions {
  email: string;
  password?: string;
  referralCode?: string;
}

export interface LoginOptions {
  email: string;
  password: string;
}

export interface AccountInfo {
  email: string;
  apiKey: string;
  credits: number;
  plan: string;
  usage?: {
    tts: number;
    stt: number;
    llm: number;
  };
}

export class Auth {
  private static signupUrl = 'https://slng.ai/signup';
  private static dashboardUrl = 'https://slng.ai/dashboard';

  static async signup(_options: SignupOptions): Promise<{ message: string; apiKey?: string }> {
    console.log(`
╔════════════════════════════════════════════╗
║     Welcome to SLNG.AI Voice Platform!     ║
╚════════════════════════════════════════════╝

Sign up at: ${this.signupUrl}

After signing up:
1. Get your API key from the dashboard
2. Initialize the SDK:

   new VoiceAI({ apiKey: 'your-api-key' });

3. Start building amazing voice experiences!

Need help? Contact founders at hello@slng.ai
`);

    return {
      message: `Please complete signup at ${this.signupUrl}`,
    };
  }

  static async getAccount(): Promise<AccountInfo | null> {
    try {
      const client = VoiceAI.getInstance();
      const config = client.getConfig();

      const response = await makeRequest({
        url: '/v1/account',
        method: 'GET',
        config,
        responseType: 'json',
      });

      return response;
    } catch (error: any) {
      if (error.message.includes('Authentication failed')) {
        console.log(`
No API key found or invalid key.

Get your API key:
1. Sign up at ${this.signupUrl}
2. Visit dashboard at ${this.dashboardUrl}
3. Copy your API key
4. Initialize: new VoiceAI({ apiKey: 'your-key' });
`);
        return null;
      }
      throw error;
    }
  }

  static async checkCredits(): Promise<number | null> {
    const account = await this.getAccount();
    return account?.credits || null;
  }

  static showDashboard(): void {
    console.log(`
╔═══════════════════════════════════════╗
║         SLNG.AI Dashboard             ║
╚═══════════════════════════════════════╝

Manage your account: ${this.dashboardUrl}

• View API keys
• Check credit balance
• Monitor usage
• Upgrade plan
• Access documentation

Need more credits? Visit ${this.dashboardUrl}/credits
Questions? Email hello@slng.ai
`);
  }

  static async showPricing(): Promise<void> {
    try {
      const { catalogManager } = await import('./utils/catalog');
      const catalog = await catalogManager.getCatalog();
      
      console.log(`
╔═══════════════════════════════════════╗
║         SLNG.AI Pricing               ║
╚═══════════════════════════════════════╝

Simple, transparent pricing:

TEXT-TO-SPEECH (TTS):`);
      
      Object.entries(catalog.tts).forEach(([_key, model]) => {
        console.log(`• ${model.name}: ${model.pricing}`);
      });
      
      console.log(`
SPEECH-TO-TEXT (STT):`);
      Object.entries(catalog.stt).forEach(([_key, model]) => {
        console.log(`• ${model.name}: ${model.pricing}`);
      });
      
      console.log(`
LANGUAGE MODELS (LLM):`);
      Object.entries(catalog.llm).forEach(([_key, model]) => {
        console.log(`• ${model.name}: ${model.pricing}`);
      });
      
      console.log(`
View full pricing: https://slng.ai/pricing
Get started: ${this.signupUrl}
`);
    } catch (error) {
      // Fallback to web link if catalog fails
      console.log(`View current pricing: https://slng.ai/pricing`);
    }
  }

  static async showModels(): Promise<void> {
    try {
      const { catalogManager } = await import('./utils/catalog');
      const catalog = await catalogManager.getCatalog();
      
      console.log(`
╔═══════════════════════════════════════╗
║      Available Voice AI Models        ║
╚═══════════════════════════════════════╝

TEXT-TO-SPEECH (TTS):`);
      
      Object.entries(catalog.tts).forEach(([key, model]) => {
        console.log(`• ${key} - ${model.description}`);
        if (model.languages) {
          console.log(`  Languages: ${model.languages.slice(0, 5).join(', ')}${model.languages.length > 5 ? '...' : ''}`);
        }
      });
      
      console.log(`
SPEECH-TO-TEXT (STT):`);
      Object.entries(catalog.stt).forEach(([key, model]) => {
        console.log(`• ${key} - ${model.description}`);
      });
      
      console.log(`
LANGUAGE MODELS (LLM):`);
      Object.entries(catalog.llm).forEach(([key, model]) => {
        console.log(`• ${key} - ${model.description}`);
      });
      
      console.log(`
Need a different model? 
Email us at hello@slng.ai

Last updated: ${new Date(catalog.lastUpdated).toLocaleDateString()}
`);
    } catch (error) {
      // Fallback to web link if catalog fails
      console.log(`View available models: https://slng.ai/models`);
    }
  }
}

export const auth = Auth;